import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Provider, Consumer } from './context';
import { HtmlElement } from '../../prop-types/Element';

/**
 * A simple component wrapper for the IntersectionObserver API
 *
 * @param {ReactElement} children - the child DOM nodes
 * @param {object} opts - an object containing IntersectionObserver options (https://developer.mozilla.org/en-US/docs/Web/API/Intersection_Observer_API)
 * @param {DOMNode} target - the element that IntersectionOberver watches. Accepts a ref or DOMNode
 */
const BeeIntersectionObserver = ({ children, opts, target, onIntersect }) => {
  const [isIntersecting, setIsIntersecting] = useState(null);
  const [boundingClientRect, setBoundingClientRect] = useState(null);

  useEffect(() => {
    // If a ref is passed, look for current
    if (
      target &&
      (target instanceof HTMLElement ||
        (target.current && target.current instanceof HTMLElement))
    ) {
      const cb = (entries) => {
        entries.forEach((entry) => {
          setIsIntersecting(entry.isIntersecting);
          setBoundingClientRect(entry.boundingClientRect);
        });
      };

      const observer = new IntersectionObserver(cb, opts);
      observer.observe(target.current || target);
    }
  }, [target]);

  useEffect(() => {
    if (onIntersect) onIntersect(isIntersecting);
  }, [isIntersecting]);

  return (
    <>
      <Provider value={{ isIntersecting, boundingClientRect }}>
        <Consumer>{children}</Consumer>
      </Provider>
    </>
  );
};

export default BeeIntersectionObserver;

BeeIntersectionObserver.propTypes = {
  /**
   * The child elements to render, passed as a function so it can be consumed by Context
   */
  children: PropTypes.func.isRequired,
  opts: PropTypes.shape({
    /**
     * The element that is used as the viewport for checking visibility of the target.
     */
    root: PropTypes.instanceOf(HtmlElement),
    /**
     * Margin around the root. Similar to CSS Mmrgin property
     */
    rootMargin: PropTypes.string,
    /**
     * Indicates the percentage of visibility at which the observers callback should be executed
     */
    threshold: PropTypes.number
  }),
  /**
   * The element to watch
   */
  target: PropTypes.oneOfType([
    PropTypes.instanceOf(HtmlElement),
    PropTypes.object
  ]),
  /**
   * Callback function to run when the element intersects
   */
  onIntersect: PropTypes.func
};

BeeIntersectionObserver.defaultProps = {
  opts: {
    root: null,
    rootMargin: '0px 0px 0px 0px',
    threshold: 0
  },
  onIntersect: null,
  target: null
};
